import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '../api/client'
import { TOKEN_VALIDATION_ENDPOINT } from '../api/config'

interface AuthContextType {
  isAuthenticated: boolean
  login: (token: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

const TOKEN_KEY = 'codewords_admin_token'
const EXPIRY_KEY = 'codewords_admin_token_expiry'
const TOKEN_EXPIRY_MS = 3600000 // 1 hour

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const token = getToken()
    setIsAuthenticated(!!token)
  }, [])

  const login = async (token: string) => {
    try {
      // Validate token by making a test API call
      await apiClient.get(TOKEN_VALIDATION_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      // Token is valid, store it
      const expiry = Date.now() + TOKEN_EXPIRY_MS
      sessionStorage.setItem(TOKEN_KEY, token)
      sessionStorage.setItem(EXPIRY_KEY, expiry.toString())
      setIsAuthenticated(true)
    } catch (error: any) {
      console.error('Login error:', error)
      const message = error.response?.data?.error || error.message || 'Authentication failed'
      throw new Error(`Invalid token: ${message}`)
    }
  }

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(EXPIRY_KEY)
    setIsAuthenticated(false)
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function getToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY)
  const expiry = sessionStorage.getItem(EXPIRY_KEY)

  if (!token || !expiry) return null

  if (Date.now() > parseInt(expiry)) {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(EXPIRY_KEY)
    return null
  }

  return token
}
